import { newPropertyFilter } from '../request-filters';
import { applyFeatureStates, copyAllFeatureState, setFeatureStateForSources } from '../features';
import { locationSources } from '../layers';
import { applyInputState, captureInputState, clearInputState } from '../../forms/input';
import { onMapMoveEndLoadFeatures } from '../events/move-end';
import { addAreaToMap, removeAreaState } from '../area';
import { coordinatesToFeature } from '../geojson';
import { removeElements } from '../../dom/remove';
import { urlSearchParamsAsObject } from '../../url/search-params';
import { getSaveSearchState } from '../../search/state';
import { boundsAsPolygonCSV, fitBoundsAsync, newBoundingBoxEvent, newBoundingBoxOptions } from '../bounding-box';
import { removeRadiusState } from '../radius';
import { deactiveFeaturesForSource, removeApproxLocationCircle, setLayer } from '../layers';
import PropertyResponse from '../models/property-response';
import { upsertHiddenInput } from '../../dom/create';
import { toFixed } from '../spatial';
import { getFeatureRequestsForArea } from '../area';
import { Region } from '../../locations';
import PropertySearchFilter, { locationFilters } from '../../property/filters';
import { getState } from '../../routing/history';
import Logger from '../../logging/logger';
import { triggerPageView } from '../../analytics/ga/events';

class SearchThisArea {
    constructor( mapSearch, options = {} ) {
        this.options = Object.assign( {}, SearchThisArea.defaultOptions, options );
        this.mapSearch = mapSearch;
        this.state = this.options.initialState;
        this.searchURL = this.options.searchURL;
        this.stateBeforeActivation = {};
        this.onMoveEnd = this.mapSearch.proxyEvent( this.mapSearch, onMapMoveEndLoadFeatures );
        this.handleSearch = this.handleSearch.bind( this );
        this.toggleState = this.toggleState.bind( this );
    }

    init( map ) {
        const controlContainer = document.querySelector( '.twc__map-search-control-container-bottom' );

        if ( !controlContainer ) {
            return;
        }

        this.map = map;

        document.documentElement.classList.add( `mapsearch__${SearchThisArea.id}` );

        this.container = document.createElement( 'div' );
        this.container.className = 'searchthisarea__container';

        const initialSearchFilter = PropertySearchFilter.fromURLSearchParams( new URLSearchParams( window.location.search ) );

        if ( initialSearchFilter.area.length > 0 && initialSearchFilter.viewMode !== SearchThisArea.id ) {
            this.container.style.display = 'none';
        }

        if ( initialSearchFilter.anyLocationFilters() ) {
            this.container.style.display = 'none';
        }

        this.initiateButton = document.createElement( 'label' );
        this.initiateButton.className = 'twc__map-search-item searchthisarea__toggle';
        this.initiateButton.innerHTML = `<span>${this.mapSearch.getTemplateString( 'searchThisAreaToggle' )}</span>`;
        this.initiateButton.title = this.mapSearch.getTemplateString( 'searchThisAreaToggle' );
        this.initiateButton.setAttribute( 'for', 'search-this-area-toggle' );

        this.checkbox = document.createElement( 'input' );
        this.checkbox.className = 'searchthisarea__checkbox';
        this.checkbox.id = 'search-this-area-toggle';
        this.checkbox.type = 'checkbox';
        this.checkbox.checked = this.state.active;
        this.checkbox.addEventListener( 'change', this.toggleState );

        this.initiateButton.appendChild( this.checkbox );
        this.container.appendChild( this.initiateButton );

        window.addEventListener( 'twc:mapsearch:search-this-area:deactivate', this.reset.bind( this ) );

        window.addEventListener( 'twc:saved-change', this.handleSave.bind( this ) );

        this.mapSearch.on( 'twc:mapsearch:updated', this.toggleVisibility.bind( this ) );

        this.mapSearch.on( 'twc:mapsearch:search-this-area:activate', this.handleActivate.bind( this ) );

        controlContainer.appendChild( this.container );
    }

    toggleVisibility() {
        let display = '';

        const filter = PropertySearchFilter.fromURLSearchParams( new URLSearchParams( window.location.search ) );

        if ( filter.area.length > 0 && !this.state.active ) {
            display = 'none';
        }

        this.container.style.display = display;
    }

    async handleSave( event ) {
        const { saved, type, value } = event.detail;

        if ( type !== 'property-search' || !( 'Area' in value ) ) {
            return;
        }

        if ( !saved && !this.state.active ) {
            this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:activate', {
                bubbles: true,
                detail: {
                    triggerStateChange: true,
                },
            } ) );
            return;
        }

        if ( saved && this.state.active ) {
            this.mapSearch.setLoading( true );

            this.container.style.display = 'none';

            const coordinates = value.Area.coordinates.map( ( coordPair ) => {
                return [
                    coordPair.lon,
                    coordPair.lat,
                ];
            } );

            const feature = coordinatesToFeature( coordinates );

            const areaOptions = {
                closeEventDetail: {},
            };

            if ( this.stateBeforeActivation.locationID &&
                this.stateBeforeActivation.locationFreeText
            ) {
                areaOptions.closeEventDetail = {
                    locationID: {
                        value: SearchThisArea.id,
                    },
                    locationFreeText: {
                        value: this.mapSearch.getTemplateString( 'searchThisAreaTypeaheadLabel' ),
                    },
                };
            }

            addAreaToMap( this.mapSearch, feature, areaOptions );

            const areaBboxOptions = {
                padding: {
                    top: 50,
                    right: 50,
                    bottom: 50,
                    left: 50,
                },
            };

            const bboxEvent = newBoundingBoxEvent( {
                isAreaRectangle: false,
            } );

            let rect = window.document.documentElement.getBoundingClientRect();

            if ( rect.height !== window.innerHeight ) {
                const resizeFinished = () => {
                    return new Promise( ( resolve ) => {
                        const int = window.setInterval( () => {
                            rect = window.document.documentElement.getBoundingClientRect();

                            if ( parseInt( rect.height ) === window.innerHeight ) {
                                window.clearInterval( int );
                                resolve();
                            }
                        }, 50 );
                    } );
                };

                await resizeFinished();
            }

            const idle = () => {
                return new Promise( ( resolve ) => {
                    this.map.on( 'idle', () => {
                        resolve();
                    } );
                } );
            };

            await idle();

            await fitBoundsAsync( this.mapSearch, {
                bbox: feature.bbox,
                options: areaBboxOptions,
                event: bboxEvent,
            } );

            removeElements( 'input[name=area]' );

            const areaInput = document.createElement( 'input' );
            areaInput.type = 'hidden';
            areaInput.name = 'area';
            areaInput.value = coordinates;
            this.mapSearch.baseForm.appendChild( areaInput );

            this.reset();

            this.mapSearch.movefuncs = this.stateBeforeActivation.movefuncs;

            this.mapSearch.baseForm.dispatchEvent( new CustomEvent( 'twc:mapsearch:form-submit', {
                bubbles: true,
                detail: {
                    addArea: false,
                    focusMapToSearch: false,
                },
            } ) );
        }

        this.container.style.display = 'none';

        this.mapSearch.setLoading( false );
    }

    async toggleState() {
        const options = Object.assign( {}, SearchThisArea.defaultSearchOptions, {
            triggerStateChange: true,
            showNotification: true,
        } );

        await this.setState( { active: !this.state.active }, options );
    }

    async handleActivate( event ) {
        const options = Object.assign( {}, SearchThisArea.defaultSearchOptions, event.detail );

        Logger.info( `[map] Handing ${SearchThisArea.id} Activation Event`, event, options );

        const { originalEvent } = event.detail || {};

        if ( originalEvent && originalEvent.type === 'popstate' ) {
            options.triggerStateChange = false;

            const bboxEvent = newBoundingBoxEvent();
            const bboxOptions = newBoundingBoxOptions();

            const { boundSW, boundNE } = originalEvent.state.meta.propertySearchParams;

            const sw = boundSW.split( ',' ).map( ( s ) => parseFloat( s ) );
            const ne = boundNE.split( ',' ).map( ( s ) => parseFloat( s ) );

            const bbox = [
                [ sw[ 1 ], sw[ 0 ] ],
                [ ne[ 1 ], ne[ 0 ] ],
            ];

            options.addMoveEndListener = false;

            this.map.on( 'moveend', this.onMoveEnd );

            await fitBoundsAsync( this.mapSearch, {
                bbox: bbox,
                options: bboxOptions,
                event: bboxEvent,
            } );
        }

        if ( options.triggerStateChangeOnFirstMove ) {
            this.triggerStateChangeOnFirstMove = true;
        }

        await this.setState( { active: true }, options );
    }

    async activate( options ) {
        Logger.info( `[map] Activating ${SearchThisArea.id}`, options );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:hide-remove-search-area', { bubbles: true } ) );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );

        const locationFreeText = document.querySelector( '#property-search-form-base input[name=locationFreeText]' );
        const locationID = document.querySelector( '#property-search-form-base input[name=locationId]' );

        this.stateBeforeActivation = {
            featureState: copyAllFeatureState( this.map ),
            locationFreeText: captureInputState( locationFreeText ),
            locationID: captureInputState( locationID ),
            movefuncs: this.mapSearch.movefuncs,
        };

        this.mapSearch.currentLocation = null;
        this.mapSearch.movefuncs = [];

        this.options.eventHandlersToDisable.forEach( ( event ) => {
            this.map.off( event.name, event.handler );
        } );

        this.map.on( 'moveend', this.handleSearch );

        if ( options.addMoveEndListener ) {
            this.map.on( 'moveend', this.onMoveEnd );
        }

        if ( locationFreeText && locationID ) {
            clearInputState( locationFreeText, true, true, {
                previousValue: true,
                tsGeo: true,
            } );
            clearInputState( locationID );
        }

        [
            this.mapSearch.baseForm,
            this.mapSearch.popupForm,
        ].forEach( ( form ) => {
            form.dispatchEvent( new CustomEvent( 'twc:typeahead:set-value', {
                bubbles: true,
                detail: {
                    label: this.mapSearch.getTemplateString( 'searchThisAreaTypeaheadLabel' ),
                    state: {
                        value: SearchThisArea.id,
                    },
                },
            } ) );
        } );

        removeRadiusState( this.map, true );

        removeApproxLocationCircle( this.map );

        setFeatureStateForSources( this.map, locationSources, {
            active: false,
        } );

        const facetOpener = document.querySelector( '#region-board-opener' );

        if ( facetOpener ) {
            facetOpener.style.display = 'none';
        }

        const bounds = this.map.getBounds();
        const boundSW = toFixed( bounds.getSouthWest().lat ) + ',' + toFixed( bounds.getSouthWest().lng );
        const boundNE = toFixed( bounds.getNorthEast().lat ) + ',' + toFixed( bounds.getNorthEast().lng );

        upsertHiddenInput( this.mapSearch.popupForm, 'boundSW', boundSW );
        upsertHiddenInput( this.mapSearch.popupForm, 'boundNE', boundNE );
        upsertHiddenInput( this.mapSearch.baseForm, 'boundSW', boundSW );
        upsertHiddenInput( this.mapSearch.baseForm, 'boundNE', boundNE );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:update-filter-count-badges', { bubbles: true } ) );

        if ( options.search ) {
            await this.search( {
                triggerStateChange: options.triggerStateChange,
                fetchFeatures: options.fetchFeatures,
                setPreviousSearch: options.setPreviousSearch,
            } );
        }

        this.mapSearch.locationIndicator.update( this.mapSearch.getTemplateString( 'searchThisAreaNotification' ), {
            autohide: true,
            timeout: 5000,
            block: true,
        } );
    }

    deactivate( options = {} ) {
        Logger.info( `[map] Deactivating ${SearchThisArea.id}` );

        options = {
            searchOptions: {
                addArea: false,
                focusMapToSearch: false,
                resetLayers: false,
                clearMoveFuncs: false,
                recalculateBboxAfterAnimation: true,
            },
            ...options,
        };

        removeElements( 'input[name=boundSW][type=hidden]' );
        removeElements( 'input[name=boundNE][type=hidden]' );

        this.map.off( 'moveend', this.handleSearch );

        this.map.off( 'moveend', this.onMoveEnd );

        this.options.eventHandlersToDisable.forEach( ( event ) => {
            this.map.off( event.name, event.handler );
            this.map.on( event.name, event.handler );
        } );

        this.mapSearch.movefuncs = this.stateBeforeActivation.movefuncs;

        const locationID = document.querySelector( '#property-search-form-base input[name=locationId]' );

        if ( this.stateBeforeActivation.locationID && locationID ) {
            applyInputState( locationID, this.stateBeforeActivation.locationID );
        }

        const locationFreeText = document.querySelector( '#property-search-form-base input[name=locationFreeText]' );

        if ( this.stateBeforeActivation.locationFreeText && locationFreeText ) {
            applyInputState( locationFreeText, this.stateBeforeActivation.locationFreeText );
        }

        applyFeatureStates( this.map, this.stateBeforeActivation.featureState );

        this.stateBeforeActivation = {};

        this.mapSearch.baseForm.dispatchEvent( new CustomEvent( 'twc:mapsearch:form-submit', {
            bubbles: true,
            detail: options.searchOptions,
        } ) );

        if ( options.showNotification ) {
            this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-notification', {
                bubbles: true,
                detail: {
                    id: 'discover-map-search',
                    msg: this.mapSearch.getTemplateString( 'discover' ),
                    options: {
                        autohide: false,
                        btn: ( handler ) => {
                            const hideBtn = document.createElement( 'button' );
                            hideBtn.className = 'twc__map-search-item twc__map-search-item--dark mt-sm';
                            hideBtn.type = 'button';
                            hideBtn.innerHTML = this.mapSearch.getTemplateString( 'discoverClose' );
                            hideBtn.dataset.forNotification = 'discover-map-search';
                            hideBtn.addEventListener( 'click', handler.hide );
                            return hideBtn;
                        },
                    },
                },
            } ) );
        }
    }

    async setState( state, options = SearchThisArea.defaultSearchOptions ) {
        this.state = {
            ...this.state,
            ...state,
        };

        const { active } = this.state;

        this.checkbox.checked = active;

        this.container.dispatchEvent( new CustomEvent( 'twc:mapsearch:toggle-component', {
            bubbles: true,
            detail: {
                active: active,
                componentID: SearchThisArea.id,
            },
        } ) );

        let eventType;

        if ( active ) {
            eventType = 'activated';
            await this.activate( options );
        } else {
            eventType = 'deactivated';
            this.deactivate( options );
        }

        this.container.dispatchEvent( new CustomEvent( 'twc:mapsearch:toggle-component', {
            bubbles: true,
            detail: {
                active: active,
                componentID: SearchThisArea.id,
            },
        } ) );

        this.container.dispatchEvent( new CustomEvent( `twc:mapsearch:search-this-area:${eventType}`, { bubbles: true } ) );
    }

    /**
     * @param {Object} event - the fitBounds event object
     */
    async handleSearch() {
        const bounds = this.map.getBounds();
        const boundSW = toFixed( bounds.getSouthWest().lat ) + ',' + toFixed( bounds.getSouthWest().lng );
        const boundNE = toFixed( bounds.getNorthEast().lat ) + ',' + toFixed( bounds.getNorthEast().lng );

        upsertHiddenInput( this.mapSearch.popupForm, 'boundSW', boundSW );
        upsertHiddenInput( this.mapSearch.popupForm, 'boundNE', boundNE );
        upsertHiddenInput( this.mapSearch.baseForm, 'boundSW', boundSW );
        upsertHiddenInput( this.mapSearch.baseForm, 'boundNE', boundNE );

        const options = {
            triggerStateChange: false,
        };

        if ( this.triggerStateChangeOnFirstMove ) {
            options.triggerStateChange = true;
            this.triggerStateChangeOnFirstMove = false;
        }

        await this.search( options );
    }

    async search( options = {} ) {
        options = Object.assign( {}, {
            triggerStateChange: false,
            fetchFeatures: false,
            setPreviousSearch: false,
        }, options );

        this.mapSearch.setLoading( true );

        let initialSearch = false;

        if ( !this.mapSearch.baseForm.dataset.initialSearchStarted && !this.mapSearch.popupForm.dataset.initialSearchStarted ) {
            initialSearch = true;
            this.mapSearch.baseForm.dataset.initialSearchStarted = 'true';
            this.mapSearch.popupForm.dataset.initialSearchStarted = 'true';
        }

        const existingArea = this.mapSearch.baseForm.querySelector( '[name=area]' );

        if ( existingArea && existingArea.value.length > 0 ) {
            removeAreaState( this.map );
        }

        const bounds = this.map.getBounds();

        const propertyFilters = newPropertyFilter( {
            onlyProperties: true,
        }, this.mapSearch.baseForm );

        if ( propertyFilters.has( 'boundSW' ) && propertyFilters.has( 'boundNE' ) ) {
            const boundSW = toFixed( bounds.getSouthWest().lat ) + ',' + toFixed( bounds.getSouthWest().lng );
            const boundNE = toFixed( bounds.getNorthEast().lat ) + ',' + toFixed( bounds.getNorthEast().lng );
            propertyFilters.set( 'boundSW', boundSW );
            propertyFilters.set( 'boundNE', boundNE );
        }

        if ( !propertyFilters.has( 'area' ) ) {
            const area = boundsAsPolygonCSV( bounds.toArray() );
            propertyFilters.set( 'area', area );
        }

        locationFilters.all().forEach( ( name ) => propertyFilters.delete( name ) );

        const searchOptions = {
            fetchProperties: true,
            propertySearchURL: this.searchURL,
            propertySearchParams: propertyFilters,
        };

        const meta = {
            triggerStateChange: false,
            addArea: false,
        };

        const paramsToChange = {
            'currentLocationID': 0,
            'currentLocationType': '',
        };

        for ( const [ key, value ] in paramsToChange ) {
            searchOptions.propertySearchParams.set( key, value );
        }

        locationSources.forEach( ( src ) => deactiveFeaturesForSource( this.map, src ) );

        if ( options.fetchFeatures ) {
            const requests = await getFeatureRequestsForArea( this.mapSearch, propertyFilters.get( 'area' ) );

            if ( requests.length === 0 ) {
                if ( !this.map.getSource( Region ) )  {
                    this.map.addSource( Region, {
                        type: 'geojson',
                        data: this.mapSearch.JSON[ Region ],
                        tolerance: 0,
                    } );
                }

                const regionLayer =  this.map.getLayer( `${Region}:outline` );

                if ( !regionLayer ) {
                    setLayer( this.mapSearch, {
                        name: Region,
                        source: Region,
                    } );
                } else {
                    this.map.setLayoutProperty( `${Region}:outline`, 'visibility', 'visible', this.mapSearch.debug );
                }
            } else {
                meta.asyncTasks = [
                    ...meta.asyncTasks || [],
                    ...requests,
                ];
            }
        }

        const results = await this.mapSearch.update( searchOptions, meta );

        const setPrevious = (
            ( this.mapSearch.previousSearchState === null && initialSearch ) ||
            options.setPreviousSearch
        );

        if ( setPrevious ) {
            this.mapSearch.previousSearchState = propertyFilters.toString();
        }

        const response = results.find( ( r ) => r instanceof PropertyResponse ).response;

        if ( !response ) {
            throw new Error( 'failed to find property response' );
        }

        const currentPath = response.searchURLs[ this.mapSearch.language ];

        const newURL = new URL( window.location.origin + currentPath );

        const searchParams = urlSearchParamsAsObject( searchOptions.propertySearchParams );

        if ( options.triggerStateChange ) {
            this.mapSearch.previousSearchState = searchOptions.propertySearchParams.toString();

            document.dispatchEvent( new CustomEvent( 'twc:manual-navigation', {
                bubbles: true,
                detail: {
                    url: newURL.toString(),
                    title: response.title,
                    description: response.description,
                    hasFragment: false,
                    fragment: '',
                    isRoutedPopup: false,
                    scrollPosition: 0,
                    meta: {
                        firstMapView: false,
                        viewMode: SearchThisArea.id,
                        propertySearchParams: searchParams,
                        saveState: getSaveSearchState( 'property-search' ),
                        location: null,
                    },
                    manualPopstate: true,
                    onlyUpdateState: true,
                },
            } ) );

            if ( response.contentGroup && response.contentGroup.length > 0 ) {
                triggerPageView( {
                    'page': newURL.pathname + '?' + newURL.search,
                    'contentGroup': response.contentGroup,
                } );
            }
        } else {
            const currentState = getState();

            const updatedState = {
                ...currentState,
                url: newURL.toString(),
                meta: {
                    ...currentState.meta,
                    viewMode: SearchThisArea.id,
                },
            };

            window.router.Replace( 'current', Object.assign( {}, currentState, updatedState ) );
        }

        this.mapSearch.setLoading( false );
    }

    reset() {
        if ( !this.state.active ) {
            return;
        }

        this.state.active = false;
        this.checkbox.checked = false;

        this.container.dispatchEvent( new CustomEvent( 'twc:mapsearch:toggle-component', {
            bubbles: true,
            detail: {
                active: false,
                componentID: SearchThisArea.id,
            },
        } ) );

        removeElements( 'input[name=boundSW][type=hidden]' );
        removeElements( 'input[name=boundNE][type=hidden]' );

        this.map.off( 'moveend', this.handleSearch );

        this.map.off( 'moveend', this.onMoveEnd );

        this.options.eventHandlersToDisable.forEach( ( event ) => {
            this.map.off( event.name, event.handler );
            this.map.on( event.name, event.handler );
        } );

        const facetOpener = document.querySelector( '#region-board-opener' );

        if ( facetOpener ) {
            facetOpener.style.display = '';
        }
    }
}

SearchThisArea.id = 'search-this-area';

SearchThisArea.defaultSearchOptions = {
    triggerStateChange: false,
    triggerStateChangeOnFirstMove: false,
    addMoveEndListener: true,
    search: true,
    showBackButton: true,
    fetchFeatures: false,
    setPreviousSearch: false,
    showNotification: false,
};

SearchThisArea.defaultOptions = {
    searchURL: null,
    initialState: {
        active: false,
        toggledByUser: false,
    },
    eventHandlersToDisable: [],
};

export default SearchThisArea;
